import { ExportTitles } from '@aibees/elephant/types/bootstrap';
import { TemplateCategory, TemplateOption } from '@aibees/shared/features';
import { TransformedData } from '../types';

/**
 * Collects every valid export column key from the bootstrap `export.titles`
 * payload. The column keys are the same values used as `OptionType` field
 * identifiers throughout the export dialog, so this set is the single source of
 * truth for which fields the backend still accepts.
 */
export const getValidExportFieldKeys = (
  titles: ExportTitles | undefined
): Set<string> => {
  const keys = new Set<string>();
  Object.values(titles ?? {}).forEach((category) => {
    Object.keys(category?.columns ?? {}).forEach((columnKey) =>
      keys.add(columnKey)
    );
  });
  return keys;
};

/**
 * Collects the keys of every field currently flagged as "coming soon".
 *
 * Coming-soon status is a frontend-only concept resolved from feature flags in
 * `convertOptionsToTemplateCategories` (the bootstrap `comingSoon` flag is not
 * used), so it must be read off the already-converted template categories
 * rather than the raw bootstrap payload. These fields still exist in bootstrap
 * — the backend accepts them — but they must never appear as columns in the
 * exported file.
 */
export const getComingSoonFieldKeys = (
  categories: TemplateCategory[] | undefined
): Set<string> => {
  const keys = new Set<string>();
  categories?.forEach((category) => {
    category.entries?.forEach((entry) => {
      entry.fields?.forEach((field) => {
        if (field.comingSoon && field.type) {
          keys.add(field.type as unknown as string);
        }
      });
    });
  });
  return keys;
};

/**
 * Removes fields that must not be sent to the export backend from a built
 * `setting` payload. Two kinds of field are dropped:
 *
 * 1. Keys no longer present in bootstrap (`!validKeys.has(key)`). Export
 *    templates are intentionally not versioned, so a template saved before a
 *    column was removed from bootstrap (e.g. `FOLLOWER_COUNT`) still carries
 *    the stale key, which makes the backend reject the entire request.
 * 2. Keys flagged as coming-soon (`comingSoonKeys.has(key)`). These still exist
 *    in bootstrap so the backend accepts them, but they were only ever added to
 *    a template through a bug (e.g. Select All) and must not leak into the file.
 *
 * This is the last safeguard before the export request goes out. If `validKeys`
 * is empty (bootstrap not loaded yet) the setting is returned unchanged, so a
 * transient empty response never strips every field.
 */
export const stripUnexportableFields = (
  setting: TransformedData,
  validKeys: Set<string>,
  comingSoonKeys?: Set<string>
): TransformedData => {
  if (validKeys.size === 0) return setting;

  const filterFields = (fields: TemplateOption[]): TemplateOption[] =>
    fields.filter((field) => {
      const key = field as unknown as string;
      return validKeys.has(key) && !comingSoonKeys?.has(key);
    });

  return Object.entries(setting).reduce<TransformedData>(
    (acc, [categoryKey, value]) => {
      acc[categoryKey] = Array.isArray(value)
        ? value.map((entry) => ({
            ...entry,
            fields: filterFields(entry.fields),
          }))
        : { ...value, fields: filterFields(value.fields) };
      return acc;
    },
    {}
  );
};
